/**
 * Web sound adapter — the real uisfx player (pack: `zen`), bridged to the
 * shared sound core's `SoundPlayerAdapter` interface.
 *
 * - ONE module-level lazy singleton: created on first use in the browser,
 *   never during SSR (window guard), never duplicated by remounts or React
 *   Strict Mode (no effect-scoped construction anywhere).
 * - Web Audio unlock: `createUISFX` is instantiated lazily and
 *   `player.unlock()` is called from real user gestures — initWebSounds
 *   installs one-shot pointerdown/keydown listeners, and `unlockAudio()`
 *   remains callable from click handlers. Until unlocked, uisfx `play()`
 *   returns `null` (async cues are suppressed, not queued) and the adapter
 *   passes that through.
 * - Volume/enabled state land here from the persisted prefs via the shared
 *   core's `applySoundVolume` / `applySoundsEnabled`.
 */

import {
  createBufferEngine,
  type LocalSoundAsset,
  type SoundHandle,
  type SoundPlayerAdapter,
} from '@flashka/shared/sound';

import { createUISFX, type UISFXPlayer } from 'uisfx';

import { LOCAL_ASSET_URLS } from './local-assets';
import { getPack, getSoundsEnabled, getVolume } from './sound-prefs';

let player: UISFXPlayer | null = null;
/**
 * ONE AudioContext for the whole app: handed to uisfx via its `context`
 * option and reused for our local assets, so catalog cues and shipped files
 * share a clock, a destination, and a single gesture unlock.
 */
let ctx: AudioContext | null = null;
let masterVolume = getVolume();

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ??
      (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      // A browser out of audio hardware/contexts throws here; no sound then.
      return null;
    }
  }
  return ctx;
}

function getPlayer(): UISFXPlayer | null {
  if (typeof window === 'undefined') return null; // SSR: never instantiate
  if (!player) {
    const context = getContext();
    player = createUISFX({
      pack: getPack(),
      volume: getVolume(),
      enabled: getSoundsEnabled(),
      maxVoices: 4,
      ...(context ? { context } : {}),
    });
  }
  return player;
}

/**
 * Our local assets (sounds uisfx has no cue for) play through the shared
 * decoded-buffer engine — the same one the app plays EVERY cue through — on
 * the page's one context. The context is shared with uisfx, so the engine
 * never suspends it, and only a gesture may resume it (`resumeOnPlay: false`).
 */
const assetEngine = createBufferEngine<AudioBuffer, AudioContext>({
  getContext,
  decode: (asset, context) =>
    fetch(LOCAL_ASSET_URLS[asset as LocalSoundAsset])
      .then((res) => res.arrayBuffer())
      .then((data) => context.decodeAudioData(data)),
  bindEnded: (node, onEnded) => {
    const source = node as AudioBufferSourceNode;
    source.onended = onEnded;
    return () => {
      source.onended = null;
    };
  },
  resumeOnPlay: false,
});

export const webSoundAdapter: SoundPlayerAdapter = {
  play(source, opts): SoundHandle | null {
    if (source.kind === 'asset') {
      if (!getSoundsEnabled()) return null;
      return assetEngine.play(source.asset, (opts.gain ?? 1) * masterVolume);
    }
    const p = getPlayer();
    if (!p) return null;
    // A cue on a context that is not running is dropped, not queued — and it
    // must be dropped HERE: uisfx 0.4 calls `context.resume()` itself on a
    // suspended context without catching it, and Safari rejects that with
    // "InvalidStateError: Failed to start the audio device" as an unhandled
    // rejection (FLASHKA-VITE-28D). Resume it ourselves, caught; the next cue
    // plays once it is running. A null handle means "nothing played". Any
    // state but `closed` is worth the resume — Safari's `interrupted` (after a
    // call or the tab coming back) included.
    const context = getContext();
    if (context && context.state !== 'running') {
      if (context.state !== 'closed') void context.resume().catch(() => {});
      return null;
    }
    // A UI sound must never take the page down. uisfx renders a cue into an
    // AudioBuffer at the context's sample rate, and `createBuffer` THROWS
    // where the browser does not support that rate (NotSupportedError, iPad
    // Chrome) — synchronously, inside the toast that wanted a "ding", inside a
    // React effect, so the app-root error boundary replaced the whole app
    // (FLASHKA-VITE-2B8). Silence is the right failure for a sound effect.
    try {
      const playing = p.play(source.cue);
      // uisfx returns null when suppressed (disabled, voice cap). Its own
      // `ended` promise is the most accurate end-of-sound signal available —
      // pass it straight through for the core's sequencing.
      return playing ? { stop: () => playing.stop(), ended: playing.ended } : null;
    } catch {
      return null;
    }
  },
  stopAll() {
    player?.stopAll();
    assetEngine.stopAll();
  },
  setEnabled(enabled) {
    getPlayer()?.setEnabled(enabled);
  },
  setVolume(volume) {
    masterVolume = volume; // local assets scale themselves
    getPlayer()?.setVolume(volume);
  },
  setPack(pack) {
    getPlayer()?.setPack(pack);
  },
  unlock() {
    const context = getContext();
    if (context && context.state !== 'running') void context.resume().catch(() => {});
    const p = getPlayer();
    if (!p) return;
    void p.unlock().catch(() => {
      // Unlock outside a genuine gesture is expected to fail — non-fatal.
    });
  },
};

/**
 * Resume Web Audio on a real user gesture so async cues (toasts, results
 * landing after navigation) can sound. The listeners stay armed for the page's
 * life: the context can stop running again later (Safari suspends or
 * interrupts it after a call or a backgrounded tab), and only a gesture may
 * resume it. While it is running a gesture costs one state read.
 */
export function installGestureUnlock(): void {
  if (typeof document === 'undefined') return;
  const onGesture = () => {
    if (ctx?.state === 'running') return;
    const p = getPlayer();
    if (!p) return;
    void p.unlock().catch(() => {});
  };
  document.addEventListener('pointerdown', onGesture, { passive: true });
  document.addEventListener('keydown', onGesture);
}
